/**
 * StepUpModal — admin TOTP re-auth for high-risk actions.
 *
 * Usage: place once at the app root. Components trigger it through
 * `window.dispatchEvent(new CustomEvent('step-up:required', {
 *    detail: { purpose, retry } }))` — `retry` is a 0-arg function the
 * modal calls after a successful `/api/auth/mfa/step-up`.
 *
 * This avoids touching every admin button — any 403 response with
 * `code:step_up_required` from the backend can be funnelled here by
 * a shared fetch wrapper or per-call inline handler.
 */
import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Shield, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeader() {
  const tok = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

export default function StepUpModal() {
  const [pending, setPending] = useState(null);   // {purpose, retry}
  const [code, setCode]       = useState('');
  const [error, setError]     = useState('');
  const [busy, setBusy]       = useState(false);

  useEffect(() => {
    const onEvent = (e) => {
      setPending({
        purpose: e.detail?.purpose,
        retry:   e.detail?.retry,
        cancel:  e.detail?.cancel,
      });
      setCode(''); setError('');
      // Tell `fetchWithStepUp` we're alive so its safety-belt
      // timeout doesn't immediately resolve with the original 403.
      try { window.dispatchEvent(new Event('step-up:ack')); } catch (_) { /* ignore */ }
    };
    window.addEventListener('step-up:required', onEvent);
    return () => window.removeEventListener('step-up:required', onEvent);
  }, []);

  if (!pending) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/auth/mfa/step-up`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ code: code.trim(), purpose: pending.purpose }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      const retry = pending.retry;
      setPending(null); setCode('');
      // Retry the original action — best effort. Failures inside the
      // retry are the caller's responsibility.
      try { await retry?.(); } catch (_) { /* surfaced by caller */ }
    } catch (err) {
      setError(err.message || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const PURPOSE_LABELS = {
    price_multiplier_set:   'Change global price multiplier',
    price_multiplier_reset: 'Reset price multiplier to 1.0',
    company_create:         'Create a payment company',
    company_update:         'Edit a payment company',
    company_delete:         'Delete a payment company',
    customer_delete:        'Delete a customer account',
  };
  const label = PURPOSE_LABELS[pending.purpose] || 'Sensitive admin action';

  // Cancel = resolve the awaiting `fetchWithStepUp` promise (if any)
  // with the original 403 response so the caller can surface a toast
  // and stop spinning. Closing without resolving makes admin buttons
  // hang silently — observed once in production.
  const cancel = () => {
    try { pending?.cancel?.(); } catch (_) { /* ignore */ }
    setPending(null);
    setCode('');
    setError('');
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4"
      data-testid="step-up-modal"
    >
      <Card className="w-full max-w-sm p-5 space-y-3 bg-white">
        <div className="flex items-center gap-2 text-slate-800">
          <Shield className="w-4 h-4" />
          <h2 className="text-base font-semibold">Confirm with your authenticator</h2>
        </div>
        <p className="text-xs text-slate-600">
          <strong>Action:</strong> {label}.<br />
          Re-enter your current 6-digit TOTP (or a backup code) to continue. This
          unlocks sensitive actions for 5 minutes.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <Label htmlFor="stepup-code" className="text-xs">6-digit code or backup code</Label>
          <Input
            id="stepup-code"
            autoFocus
            autoComplete="one-time-code"
            inputMode="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            data-testid="step-up-code"
            required
          />
          {error && (
            <div className="text-xs text-rose-600 flex items-center gap-1" data-testid="step-up-error">
              <AlertTriangle className="w-3 h-3" /> {error}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" onClick={cancel} data-testid="step-up-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={busy || code.trim().length < 6} data-testid="step-up-submit">
              {busy ? 'Confirming…' : 'Confirm'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

/**
 * fetchWithStepUp — drop-in fetch wrapper for admin actions. If the
 * server replies 403 with `code:step_up_required`, opens the modal
 * and re-runs the original fetch once after a successful step-up.
 * Returns the eventual Response (or throws on the second failure).
 *
 * Safety belt: if the modal isn't mounted in the React tree (e.g. the
 * caller forgot to render `<StepUpModal />`), the dispatched event is
 * dropped on the floor and the awaiting promise would hang. We detect
 * this by setting a flag the moment the modal subscribes; if no
 * subscriber answered within 250 ms, we resolve with the original 403
 * so the caller can surface a toast.
 */
export async function fetchWithStepUp(input, init) {
  const doFetch = () => fetch(input, init);
  const r1 = await doFetch();
  if (r1.status !== 403) return r1;
  // Peek the body without consuming for the caller. We must clone
  // before .json() because the caller may still want to inspect r1
  // if step-up doesn't apply.
  let detail = null;
  try { detail = (await r1.clone().json()).detail; } catch (_) { /* ignore */ }
  const code = (detail && typeof detail === 'object') ? detail.code : null;
  if (code !== 'step_up_required') return r1;

  return new Promise((resolve, reject) => {
    let settled = false;
    let modalAlive = false;
    const settle = (val) => {
      if (settled) return;
      settled = true;
      resolve(val);
    };
    const onAck = () => { modalAlive = true; };
    window.addEventListener('step-up:ack', onAck);

    window.dispatchEvent(new CustomEvent('step-up:required', {
      detail: {
        purpose: detail.step_up_purpose,
        retry:   async () => {
          try {
            const r2 = await doFetch();
            settle(r2);
          } catch (e) {
            if (!settled) { settled = true; reject(e); }
          }
        },
        cancel:  () => settle(r1),  // resolve with original 403
      },
    }));

    // If no modal subscribed within 400 ms (e.g. forgotten in admin
    // route), bail out with the original 403 so the caller can stop
    // spinning. The modal acks synchronously when mounted.
    setTimeout(() => {
      window.removeEventListener('step-up:ack', onAck);
      if (!modalAlive && !settled) settle(r1);
    }, 400);
  });
}
